/**
 * Voice Memo Session Group Component
 * Groups voice memos by time proximity (sessions) with collapsible functionality
 */

import { useState } from 'react';
import { Play, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceMemoListItem } from './voice-memo-list-item';
import type { VoiceMemo, VoiceMemoGroup } from './types';

interface VoiceMemoSessionGroupProps {
  group: VoiceMemoGroup;
  onProcess: (id: string) => void;
  onProcessAll?: (ids: string[]) => void;
  onViewTranscript?: (memo: VoiceMemo) => void;
  onReviewAndFile?: (memo: VoiceMemo) => void;
  transcribingIds?: Set<string>;
  selectedMemoId?: string | null;
  defaultExpanded?: boolean;
}

export function VoiceMemoSessionGroup({
  group,
  onProcess,
  onProcessAll,
  onViewTranscript,
  onReviewAndFile,
  transcribingIds,
  selectedMemoId,
  defaultExpanded = true
}: VoiceMemoSessionGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const formatTime = (timestamp: string) => {
    if (timestamp.length >= 4) {
      return `${timestamp.substring(0, 2)}:${timestamp.substring(2, 4)}`;
    }
    return timestamp;
  };

  const unprocessedMemos = group.memos.filter(m => m.status === 'unprocessed');
  const hasUnprocessed = unprocessedMemos.length > 0;
  const isTranscribingAny = group.memos.some(m => transcribingIds?.has(m.id) || m.status === 'transcribing');

  const handleProcessAll = () => {
    if (onProcessAll && unprocessedMemos.length > 0) {
      // Process All now includes both transcription AND analysis
      onProcessAll(unprocessedMemos.map(m => m.id));
    }
  };

  return (
    <div className="mb-6">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsExpanded(!isExpanded); } }}
        className="w-full flex items-center gap-2 px-2 py-2 hover:bg-muted/50 rounded-md transition-colors mb-2 cursor-pointer"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider truncate">
            Session • {group.date} • {formatTime(group.startTime)} - {formatTime(group.endTime)} • {group.memos.length} memos
          </div>
          {hasUnprocessed && onProcessAll && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleProcessAll();
              }}
              disabled={isTranscribingAny}
              className="h-7 text-xs flex-shrink-0"
            >
              {isTranscribingAny ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Play className="w-3 h-3 mr-1.5" />
                  Process All ({unprocessedMemos.length})
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="space-y-2 pl-6">
          {group.memos.map(memo => (
            <VoiceMemoListItem
              key={memo.id}
              memo={memo}
              onProcess={onProcess}
              onViewTranscript={onViewTranscript}
              onReviewAndFile={onReviewAndFile}
              isTranscribing={transcribingIds?.has(memo.id)}
              isSelected={selectedMemoId === memo.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
